import { Box } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'
import { brandColors, brandFonts, brandLayout } from '../../styled/brand'
import { Wrap } from './Wrap'
import Kicker from './Kicker'

/**
 * A full-bleed, single-color band with the design's vertical rhythm
 * (140px desktop / 72px mobile). `overflow: hidden` clips the decorative
 * line-art some sections layer behind their content.
 */
export const Section = ({
  bg,
  children,
  sx,
  py = { xs: '72px', md: '140px' },
}: {
  bg: string
  children: React.ReactNode
  sx?: SxProps<Theme>
  py?: SxProps<Theme> | any
}) => (
  <Box sx={{ position: 'relative', overflow: 'hidden', background: bg, py, ...sx }}>
    {children}
  </Box>
)

type Variant = 'coral' | 'cream'

const variantStyles: Record<
  Variant,
  { bg: string; kicker: string; headline: string; body: string }
> = {
  coral: {
    bg: brandColors.coral,
    kicker: brandColors.creamLight,
    headline: brandColors.onCoralHeading,
    body: brandColors.onCoralBody,
  },
  cream: {
    bg: brandColors.cream,
    kicker: brandColors.coralText,
    headline: brandColors.nearBlack2,
    body: brandColors.textMuted,
  },
}

/**
 * One alternating feature row: kicker + headline + body (+ optional extras like
 * label chips) in a text column beside a phone mockup. `reverse` flips which
 * side the media sits on for rhythm. Everything collapses to a single centered
 * column below 900px. `variant` picks the coral/cream color treatment so the
 * whole section reads as one system.
 */
export const FeatureSection = ({
  variant,
  kicker,
  headline,
  body,
  media,
  reverse = false,
  headlineSize = 46,
  children,
}: {
  variant: Variant
  kicker: string
  headline: React.ReactNode
  body: React.ReactNode
  media: React.ReactNode
  reverse?: boolean
  headlineSize?: number
  children?: React.ReactNode
}) => {
  const v = variantStyles[variant]
  return (
    <Section bg={v.bg}>
      <Wrap
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: '40px', md: `${brandLayout.rowGap}px` },
          flexDirection: { xs: 'column', md: reverse ? 'row-reverse' : 'row' },
          textAlign: { xs: 'center', md: 'left' },
        }}
      >
        <Box sx={{ flex: { xs: '0 1 auto', md: '0 1 500px' }, minWidth: 0, maxWidth: '100%' }}>
          <Kicker icon color={v.kicker}>
            {kicker}
          </Kicker>
          <Box
            component="h2"
            sx={{
              margin: '24px 0 0',
              fontFamily: brandFonts.heading,
              fontWeight: 700,
              fontSize: { xs: 32, md: headlineSize },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: v.headline,
            }}
          >
            {headline}
          </Box>
          <Box
            component="p"
            sx={{
              margin: '20px 0 0',
              mx: { xs: 'auto', md: 0 },
              fontSize: 19,
              lineHeight: 1.5,
              fontWeight: 500,
              color: v.body,
              maxWidth: 460,
            }}
          >
            {body}
          </Box>
          {children}
        </Box>
        <Box
          sx={{
            flex: { xs: '0 1 auto', md: '1 1 0' },
            minWidth: 0,
            display: 'flex',
            width: { xs: '100%', md: 'auto' },
            justifyContent: {
              xs: 'center',
              md: reverse ? 'flex-start' : 'flex-end',
            },
          }}
        >
          {media}
        </Box>
      </Wrap>
    </Section>
  )
}
